import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash2, RefreshCw, X, Clock, DollarSign, Tag } from 'lucide-react';

function api(method, url, data = null, config = {}) {
  const pw = sessionStorage.getItem('admin_password');
  return axios({ method, url, data, ...config, headers: { 'x-admin-password': pw } });
}

const serviceEmojis = ['✂️', '🎨', '✨', '💆', '💧', '👰', '💅', '💇', '🌸', '💎'];

function ServiceModal({ service, onClose, onSaved }) {
  const [name, setName] = useState(service?.name || '');
  const [duration, setDuration] = useState(service?.duration_minutes || 60);
  const [price, setPrice] = useState(service?.price || '');
  const [description, setDescription] = useState(service?.description || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !duration || price === '') {
      setError('Nome, duração e preço são obrigatórios.');
      return;
    }
    if (parseFloat(price) < 0) {
      setError('O preço não pode ser negativo.');
      return;
    }
    if (parseInt(duration) < 15) {
      setError('A duração mínima é 15 minutos.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      if (service) {
        await api('put', `/api/admin/services/${service.id}`, { name, duration_minutes: duration, price, description });
      } else {
        await api('post', '/api/admin/services', { name, duration_minutes: duration, price, description });
      }
      onSaved();
      onClose();
    } catch (e) {
      setError(e.response?.data?.error || 'Erro ao salvar. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
          <h3 style={{ margin: 0 }}>{service ? 'Editar Serviço' : 'Novo Serviço'}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-gray)' }}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          <label>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <Tag size={14} color="var(--gold)" /> Nome do serviço *
            </span>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Ex: Corte Feminino"
              required
            />
          </label>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <label>
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <Clock size={14} color="var(--gold)" /> Duração (minutos) *
              </span>
              <input
                type="number"
                min="15"
                step="15"
                value={duration}
                onChange={e => setDuration(e.target.value)}
                placeholder="60"
                required
              />
            </label>

            <label>
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <DollarSign size={14} color="var(--gold)" /> Preço (R$) *
              </span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={price}
                onChange={e => setPrice(e.target.value)}
                placeholder="0,00"
                required
              />
            </label>
          </div>

          <label>
            Descrição
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Breve descrição do serviço..."
            />
          </label>

          {/* Duration preview */}
          {duration >= 15 && (
            <div style={{
              background: 'rgba(201,169,110,0.08)',
              border: '1px solid rgba(201,169,110,0.2)',
              borderRadius: '10px',
              padding: '0.75rem 1rem',
              fontSize: '0.82rem',
              color: 'var(--text-gray)',
              display: 'flex',
              gap: '1.5rem',
            }}>
              <span>⏱ <strong>{duration} min</strong> = {Math.floor(duration / 60)}h{duration % 60 > 0 ? ` ${duration % 60}min` : ''}</span>
              {price !== '' && <span>💰 <strong>R$ {parseFloat(price || 0).toFixed(2).replace('.', ',')}</strong></span>}
            </div>
          )}

          {error && (
            <div style={{
              background: '#fff0f0', border: '1px solid #ffd5d5', color: '#cc3333',
              padding: '0.65rem 1rem', borderRadius: '10px', fontSize: '0.82rem',
            }}>
              {error}
            </div>
          )}

          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose} style={{ flex: 1 }}>
              Cancelar
            </button>
            <button type="submit" className="btn-primary" disabled={loading} style={{ flex: 1 }}>
              {loading ? 'Salvando...' : service ? 'Salvar Alterações' : 'Cadastrar Serviço'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function AdminServices() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | 'new' | service object

  const load = useCallback(() => {
    setLoading(true);
    axios.get('/api/services')
      .then(r => setServices(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (id, name) => {
    if (!window.confirm(`Remover o serviço "${name}"? Esta ação não pode ser desfeita.`)) return;
    await api('delete', `/api/admin/services/${id}`);
    load();
  };

  const formatDuration = (min) => {
    const h = Math.floor(min / 60);
    const m = min % 60;
    if (h === 0) return `${m}min`;
    if (m === 0) return `${h}h`;
    return `${h}h ${m}min`;
  };

  return (
    <div className="admin-section">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <h2 className="admin-section-title" style={{ marginBottom: 0 }}>
          Serviços <span style={{ fontSize: '1rem', color: 'var(--text-gray)', fontFamily: 'Poppins' }}>({services.length})</span>
        </h2>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button
            className="btn-secondary"
            style={{ fontSize: '0.85rem', padding: '0.6rem 1.2rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}
            onClick={load}
          >
            <RefreshCw size={15} /> Atualizar
          </button>
          <button
            className="btn-primary"
            style={{ fontSize: '0.85rem', padding: '0.6rem 1.2rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}
            onClick={() => setModal('new')}
          >
            <Plus size={16} /> Novo Serviço
          </button>
        </div>
      </div>

      <div style={{
        background: 'rgba(201,169,110,0.07)',
        border: '1px solid rgba(201,169,110,0.2)',
        borderRadius: '12px',
        padding: '0.75rem 1rem',
        fontSize: '0.82rem',
        color: 'var(--text-gray)',
        marginBottom: '1.25rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
      }}>
        💡 Alterações aqui aparecem automaticamente na página de agendamento e na vitrine do site.
      </div>

      <div className="admin-table-wrap">
        {loading ? (
          <div className="empty-state">Carregando...</div>
        ) : services.length === 0 ? (
          <div className="empty-state">
            <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>✂️</div>
            <p>Nenhum serviço cadastrado.</p>
            <br />
            <button className="btn-primary" onClick={() => setModal('new')}>
              <Plus size={15} /> Cadastrar Serviço
            </button>
          </div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Serviço</th>
                <th>Descrição</th>
                <th>Duração</th>
                <th>Preço</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {services.map((s, idx) => (
                <tr key={s.id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <span style={{
                        fontSize: '1.5rem',
                        width: 40, height: 40,
                        background: 'linear-gradient(135deg, rgba(201,169,110,0.15), rgba(245,230,232,0.5))',
                        borderRadius: '10px',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                      }}>
                        {serviceEmojis[idx % serviceEmojis.length]}
                      </span>
                      <strong>{s.name}</strong>
                    </div>
                  </td>
                  <td style={{ maxWidth: '220px', color: 'var(--text-gray)', fontSize: '0.83rem' }}>
                    <span style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {s.description || <span style={{ color: '#bbb' }}>—</span>}
                    </span>
                  </td>
                  <td>
                    <span style={{
                      display: 'flex', alignItems: 'center', gap: '0.3rem',
                      background: 'rgba(41,128,185,0.1)', color: '#1a5276',
                      padding: '0.2rem 0.75rem', borderRadius: '50px',
                      fontSize: '0.82rem', fontWeight: 600, width: 'fit-content',
                    }}>
                      <Clock size={12} /> {formatDuration(s.duration_minutes)}
                    </span>
                  </td>
                  <td>
                    <span style={{
                      fontFamily: 'Playfair Display, serif',
                      fontSize: '1.15rem',
                      fontWeight: 700,
                      color: 'var(--gold-dark)',
                    }}>
                      R$ {s.price.toFixed(2).replace('.', ',')}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '0.4rem' }}>
                      <button className="btn-icon edit" title="Editar serviço" onClick={() => setModal(s)}>
                        <Pencil size={15} />
                      </button>
                      <button className="btn-icon" title="Remover serviço" onClick={() => remove(s.id, s.name)}>
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modal && (
        <ServiceModal
          service={modal === 'new' ? null : modal}
          onClose={() => setModal(null)}
          onSaved={load}
        />
      )}
    </div>
  );
}
